/**
 * Image utilities for thumbnail generation and optimization
 */

export interface ThumbnailOptions {
  maxWidth: number
  maxHeight: number
  quality: number // 0-1 for JPEG quality
  format: 'image/jpeg' | 'image/webp'
}

const DEFAULT_THUMBNAIL_OPTIONS: ThumbnailOptions = {
  maxWidth: 400,
  maxHeight: 400,
  quality: 0.8,
  format: 'image/jpeg'
}

/**
 * Generate a thumbnail from an image file
 * Returns a Blob of the resized image
 */
export async function generateThumbnail(
  file: File | Blob,
  options: Partial<ThumbnailOptions> = {}
): Promise<Blob> {
  const opts = { ...DEFAULT_THUMBNAIL_OPTIONS, ...options }
  
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    
    img.onload = () => {
      URL.revokeObjectURL(url)
      
      // Calculate new dimensions maintaining aspect ratio
      let { width, height } = img
      const aspectRatio = width / height
      
      if (width > opts.maxWidth) {
        width = opts.maxWidth
        height = width / aspectRatio
      }
      
      if (height > opts.maxHeight) {
        height = opts.maxHeight
        width = height * aspectRatio
      }
      
      // Create canvas and draw resized image
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(width)
      canvas.height = Math.round(height)
      
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Could not get canvas context'))
        return
      }
      
      // Use high quality image smoothing
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
      
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      
      // Convert to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('Could not generate thumbnail blob'))
          }
        },
        opts.format,
        opts.quality
      )
    }
    
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Could not load image for thumbnail generation'))
    }
    
    img.src = url
  })
}

/**
 * Generate a thumbnail from a URL
 * Fetches the image and creates a resized version
 */
export async function generateThumbnailFromUrl(
  imageUrl: string,
  options: Partial<ThumbnailOptions> = {}
): Promise<Blob> {
  const response = await fetch(imageUrl)
  const blob = await response.blob()
  return generateThumbnail(blob, options)
}

/**
 * Get thumbnail filename from original filename
 */
export function getThumbnailFilename(originalFilename: string): string {
  const lastDot = originalFilename.lastIndexOf('.')
  const baseName = lastDot > 0 ? originalFilename.substring(0, lastDot) : originalFilename
  return `${baseName}_thumb.jpg`
}

/**
 * Check if a URL is likely a thumbnail URL
 */
export function isThumbnailUrl(url: string): boolean {
  return url.includes('_thumb') || url.includes('/thumbnails/')
}

